import type { AdminQueryPort } from "../../contracts/admin-query-port";
import type {
  AdminLatestFailure,
  AdminUserDataDetail,
  AdminQueryEnvelope,
  AdminUserDataRiskItem,
  AdminUserLookupInput,
  AdminUserSummaryPage,
  AdminUserSummaryQuery,
  AdminUserSummary,
} from "../../contracts/admin-read-models";

export type AdminUserDataRiskLevel = "attention" | "warning" | "critical";

export type AdminUserDataRiskSummary = {
  highestLevel: AdminUserDataRiskLevel | "none";
  items: AdminUserDataRiskItem[];
};

function toPetFlowRiskItem(failure: AdminLatestFailure): AdminUserDataRiskItem | undefined {
  if (failure.signalKey !== "pet_profile") {
    return undefined;
  }

  const label =
    failure.stage === "pet_create"
      ? "Pet create failed recently"
      : failure.stage === "pet_update"
        ? "Pet profile update failed recently"
        : failure.stage === "pet_delete"
          ? "Pet delete failed recently"
          : failure.stage === "pet_photo_confirm"
            ? "Pet photo confirm failed recently"
            : undefined;

  if (!label) {
    return undefined;
  }

  return {
    code: "PET_FLOW_FAILED",
    level: "warning",
    label,
    evidence: failure.requestId ?? failure.correlationId ?? failure.errorCode,
  };
}

export async function loadUserSummary(
  port: AdminQueryPort,
  input: AdminUserLookupInput,
): Promise<AdminQueryEnvelope<AdminUserSummary>> {
  return port.getUserSummary(input);
}

export async function searchUserSummaries(
  port: AdminQueryPort,
  query: AdminUserSummaryQuery,
): Promise<AdminQueryEnvelope<AdminUserSummaryPage>> {
  if (!port.searchUserSummaries) {
    throw new Error("Admin user summary list query is not configured.");
  }
  return port.searchUserSummaries(query);
}

export async function loadUserDataDetail(
  port: AdminQueryPort,
  input: AdminUserLookupInput,
): Promise<AdminQueryEnvelope<AdminUserDataDetail>> {
  return port.getUserDataDetail(input);
}

export function classifyUserDataRisk(detail: AdminUserDataDetail): AdminUserDataRiskSummary {
  const items: AdminUserDataRiskSummary["items"] = [...(detail.user.riskSummary?.items ?? [])];
  const existingKeys = new Set(items.map((item) => `${item.code}:${item.evidence}`));

  const latestPetFailureRisk = detail.user.latestFailure
    ? toPetFlowRiskItem(detail.user.latestFailure)
    : undefined;
  if (latestPetFailureRisk && !existingKeys.has(`${latestPetFailureRisk.code}:${latestPetFailureRisk.evidence}`)) {
    items.push(latestPetFailureRisk);
  }

  const highestLevel = items.some((item) => item.level === "critical")
    ? "critical"
    : items.some((item) => item.level === "warning")
      ? "warning"
      : items.some((item) => item.level === "attention")
        ? "attention"
        : detail.user.riskSummary?.highestLevel ?? "none";

  return {
    highestLevel,
    items,
  };
}
